import { call, put, SagaGenerator, select, takeEvery } from "typed-redux-saga";
import { produce } from "immer";
import { Action } from "typescript-fsa";

import { Activity, ActivityActions, ActivitySelectors } from "@com.mgmtp.a12.client/client-core/lib/core/activity";

import { addDataHolder, createCustomNotificationAction } from "../../../app/actions";
import { localizableFromLocaleResources, RESOURCE_KEYS } from "../../../localization";
import { assertNonNullable } from "../../../utilities/assert";

import { isContactData } from "../types";
import { ContactData } from "../types";
import { LOAD_CONTACT_DATA } from "../actions";
import { loadContactData } from "../utils";

const inFlightRequests = new Set<string>();

export function* loadContactDataSaga(): SagaGenerator<void> {
	yield* takeEvery(LOAD_CONTACT_DATA, handleLoadContactData);
}

function* handleLoadContactData(action: Action<{ appKeys: string[]; activityId: string }>): SagaGenerator<void> {
	const { appKeys, activityId } = action.payload;

	const uniqueAppKeys = [...new Set(appKeys)].filter(appKey => !inFlightRequests.has(appKey));

	if (uniqueAppKeys.length === 0) {
		return;
	}
	uniqueAppKeys.forEach(appKey => inFlightRequests.add(appKey));
	let appKeysToLoad: string[] = uniqueAppKeys;
	const activity = yield* select(ActivitySelectors.activityById(activityId));
	if (!activity) {
		return;
	}
	const docRef = activity.descriptor.instance;
	assertNonNullable(docRef);
	try {
		const dataHolder = yield* select(ActivitySelectors.dataHolderByDescriptor(activityId, { type: "contactData" }));
		if (!dataHolder) {
			const contactDataholder: Activity.DataHolder = {
				descriptor: { type: "contactData" },
				dirty: false,
				savingState: "saved",
				loadingState: "missing",
				slices: {}
			};
			yield* put(addDataHolder({ dataHolder: contactDataholder, activityId }));
		}

		const newDataholder = yield* select(
			ActivitySelectors.dataHolderByDescriptor(activityId, { type: "contactData" })
		);
		const data = isContactData(newDataholder?.data)
			? newDataholder?.data
			: ({ values: {}, names: {} } as ContactData);

		appKeysToLoad = uniqueAppKeys.filter(appId => !Object.keys(data.values).includes(appId));

		if (appKeysToLoad.length === 0) {
			return;
		}
		const enumGroups = yield* call(loadContactData, appKeysToLoad, docRef);
		const currentDataHolder = yield* select(
			ActivitySelectors.dataHolderByDescriptor(activityId, { type: "contactData" })
		);
		const currentData = isContactData(currentDataHolder?.data)
			? currentDataHolder?.data
			: ({ values: {}, names: {} } as ContactData);
		const unresolvedKeys = enumGroups.filter(group => !group.resolved).map(group => group.key);
		const updatedData = produce(currentData, draft => {
			enumGroups.forEach(group => {
				draft.values[group.key] = group.values;
				if (group.name) {
					draft.names[group.key] = group.name;
				}
			});
		});
		yield* put(
			ActivityActions.setDataByDescriptor({
				activityId,
				descriptor: { type: "contactData" },
				data: updatedData
			})
		);

		if (unresolvedKeys.length > 0) {
			yield* put(
				createCustomNotificationAction({
					activityId,
					severity: "warning",
					title: localizableFromLocaleResources(RESOURCE_KEYS.features.notification.title.warning),
					message: localizableFromLocaleResources(
						RESOURCE_KEYS.features.externalEnumeration.contactList.warning,
						{ appID: { type: "plain", value: unresolvedKeys.join(", ") } }
					)
				})
			);
		}
	} catch (error) {
		const currentDataHolder = yield* select(
			ActivitySelectors.dataHolderByDescriptor(activityId, { type: "contactData" })
		);
		const currentData = isContactData(currentDataHolder?.data)
			? currentDataHolder?.data
			: ({ values: {}, names: {} } as ContactData);
		const updatedData = produce(currentData, draft => {
			appKeysToLoad.forEach(appId => {
				draft.values[appId] = [];
			});
		});
		yield* put(
			ActivityActions.setDataByDescriptor({
				activityId,
				descriptor: { type: "contactData" },
				data: updatedData
			})
		);
		yield* put(
			createCustomNotificationAction({
				activityId,
				severity: "warning",
				title: localizableFromLocaleResources(RESOURCE_KEYS.features.notification.title.warning),
				message: localizableFromLocaleResources(
					RESOURCE_KEYS.features.externalEnumeration.contactList.warning,
					{
						appID: { type: "plain", value: appKeysToLoad.join(", ") }
					}
				)
			})
		);
	} finally {
		uniqueAppKeys.forEach(appId => inFlightRequests.delete(appId));
	}
}
